<template>
  <div class="flex flex-col lg:flex-row w-full mb-6">
    <!-- Initial Calibration -->
    <div class="w-full lg:w-1/2 bg-white p-5 mt-6 lg:mr-3 rounded shadow-md">
      <div class="flex items-center mb-2">
        <h3 class="uppercase text-lg font-semibold text-primary tracking-wide mr-4">Initial Calibration</h3>
        <hr class="flex-grow border-primary border-t-2 mr-4" />
      </div>

      <div class="bg-gray-100 p-4 rounded-md mt-4">
        <div class="grid grid-cols-4 gap-4 items-center">
          <div class="col-span-2"></div>
          <div class="text-right font-medium col-span-1">Ambient Temperature:</div>
          <div class="col-span-1">
            <input
                v-model="form.initial_calibration.ambient_temperature"
                type="text"
                class="mt-1 block w-full rounded-md bge-input-spacing"
                placeholder="°C"
            />
          </div>
        </div>

        <div class="mt-4">
          <div class="grid grid-cols-3 gap-2 text-center font-medium">
            <div class="col-span-1"></div>
            <div class="col-span-1 rounded-md bg-gray-200 p-2 uppercase font-semibold text-md">True Filter Value (m-1)</div>
            <div class="col-span-1 rounded-md bg-gray-200 p-2 uppercase font-semibold text-md">Smoke Meter Reading (m-1)</div>
          </div>

          <!-- Garage Filter -->
          <div class="grid grid-cols-3 gap-2 mt-2 items-center">
            <div class="text-left font-medium">Garage Filter:</div>
            <input v-model="form.initial_calibration.garage_filter.true_filter_value" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Value" />
            <input v-model="form.initial_calibration.garage_filter.smoke_meter_reading" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Reading" />
          </div>

          <!-- Reference Filter 1 -->
          <div class="grid grid-cols-3 gap-2 mt-2 items-center">
            <div class="text-left font-medium">Reference Filter 1:</div>
            <input v-model="form.initial_calibration.reference_filter_1.true_filter_value" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Value" />
            <input v-model="form.initial_calibration.reference_filter_1.smoke_meter_reading" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Reading" />
          </div>

          <!-- Reference Filter 2 -->
          <div class="grid grid-cols-3 gap-2 mt-2 items-center">
            <div class="text-left font-medium">Reference Filter 2:</div>
            <input v-model="form.initial_calibration.reference_filter_2.true_filter_value" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Value" />
            <input v-model="form.initial_calibration.reference_filter_2.smoke_meter_reading" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Reading" />
          </div>

          <!-- Reference Filter 3 -->
          <div class="grid grid-cols-3 gap-2 mt-2 items-center">
            <div class="text-left font-medium">Reference Filter 3:</div>
            <input v-model="form.initial_calibration.reference_filter_3.true_filter_value" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Value" />
            <input v-model="form.initial_calibration.reference_filter_3.smoke_meter_reading" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Reading" />
          </div>
        </div>
      </div>
    </div>

    <!-- Final Calibration -->
    <div class="w-full lg:w-1/2 bg-white p-5 mt-6 lg:ml-3 rounded shadow-md">
      <div class="flex items-center mb-2">
        <h3 class="uppercase text-lg font-semibold text-primary tracking-wide mr-4">Final Calibration</h3>
        <hr class="flex-grow border-primary border-t-2 mr-4" />
        <div class="flex items-center">
          <AppButton color="green" label="Copy" @click="copyInitialToFinal" :icon="true" class="mr-2">
            <template #icon>
              <Icon iconType="copy-plus" class="mr-2" />
            </template>
          </AppButton>
        </div>
      </div>

      <div class="bg-gray-100 p-4 rounded-md mt-4">
        <div class="grid grid-cols-4 gap-4 items-center">
          <div class="col-span-2"></div>
          <div class="text-right font-medium col-span-1"></div>
          <div class="col-span-1" style="visibility: hidden;">
            <input type="text" class="mt-1 block w-full rounded-md bge-input-spacing" placeholder="°C" />
          </div>
        </div>

        <div class="mt-4">
          <div class="grid grid-cols-3 gap-2 text-center font-medium">
            <div class="col-span-1"></div>
            <div class="col-span-1 rounded-md bg-gray-200 p-2 uppercase font-semibold text-md">True Filter Value (m-1)</div>
            <div class="col-span-1 rounded-md bg-gray-200 p-2 uppercase font-semibold text-md">Smoke Meter Reading (m-1)</div>
          </div>

          <!-- Garage Filter -->
          <div class="grid grid-cols-3 gap-2 mt-2 items-center">
            <div class="text-left font-medium">Garage Filter:</div>
            <input v-model="form.final_calibration.garage_filter.true_filter_value" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Value" />
            <input v-model="form.final_calibration.garage_filter.smoke_meter_reading" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Reading" />
          </div>

          <!-- Reference Filter 1 -->
          <div class="grid grid-cols-3 gap-2 mt-2 items-center">
            <div class="text-left font-medium">Reference Filter 1:</div>
            <input v-model="form.final_calibration.reference_filter_1.true_filter_value" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Value" />
            <input v-model="form.final_calibration.reference_filter_1.smoke_meter_reading" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Reading" />
          </div>

          <!-- Reference Filter 2 -->
          <div class="grid grid-cols-3 gap-2 mt-2 items-center">
            <div class="text-left font-medium">Reference Filter 2:</div>
            <input v-model="form.final_calibration.reference_filter_2.true_filter_value" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Value" />
            <input v-model="form.final_calibration.reference_filter_2.smoke_meter_reading" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Reading" />
          </div>

          <!-- Reference Filter 3 -->
          <div class="grid grid-cols-3 gap-2 mt-2 items-center">
            <div class="text-left font-medium">Reference Filter 3:</div>
            <input v-model="form.final_calibration.reference_filter_3.true_filter_value" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Value" />
            <input v-model="form.final_calibration.reference_filter_3.smoke_meter_reading" type="text" class="col-span-1 rounded-md bge-input-spacing" placeholder="Reading" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import notificationMixin from "@/mixins/notificationMixin";
import Icon from "@/components/Icon.vue";
import AppButton from "@/components/Button.vue";

function blankForm() {
  return {
    initial_calibration: {
      ambient_temperature: null,
      garage_filter: { true_filter_value: null, smoke_meter_reading: null },
      reference_filter_1: { true_filter_value: null, smoke_meter_reading: null },
      reference_filter_2: { true_filter_value: null, smoke_meter_reading: null },
      reference_filter_3: { true_filter_value: null, smoke_meter_reading: null }
    },
    final_calibration: {
      ambient_temperature: null,
      garage_filter: { true_filter_value: null, smoke_meter_reading: null },
      reference_filter_1: { true_filter_value: null, smoke_meter_reading: null },
      reference_filter_2: { true_filter_value: null, smoke_meter_reading: null },
      reference_filter_3: { true_filter_value: null, smoke_meter_reading: null }
    }
  };
}

export default {
  name: "SmokeMeter",
  components: { AppButton, Icon },
  mixins: [notificationMixin],

  props: {
    // parent binds like: v-model:calibration_details="certificate.details"
    calibration_details: {
      type: Object,
      default: function () { return {}; }
    }
  },
  emits: ["update:calibration_details"],

  data() {
    return {
      form: blankForm(),
      hasUserEdited: false
    };
  },

  mounted() {
    this.seedFrom(this.calibration_details);
  },

  watch: {
    calibration_details: {
      deep: true,
      handler(val) {
        if (!val) return;
        if (this.hasUserEdited) return;
        this.seedFrom(val);
      }
    },
    form: {
      deep: true,
      handler(v) {
        this.hasUserEdited = true;
        this.$emit("update:calibration_details", JSON.parse(JSON.stringify(v)));
      }
    }
  },

  methods: {
    seedFrom(src) {
      const safe = src && typeof src === "object" && Object.keys(src).length ? src : blankForm();

      function ensure(obj, fallback) {
        return obj && typeof obj === "object" ? obj : fallback;
      }

      const i = ensure(safe.initial_calibration, blankForm().initial_calibration);
      const f = ensure(safe.final_calibration, blankForm().final_calibration);

      function mergeRow(row, def) {
        return {
          true_filter_value: (row && row.true_filter_value) != null ? row.true_filter_value : def.true_filter_value,
          smoke_meter_reading: (row && row.smoke_meter_reading) != null ? row.smoke_meter_reading : def.smoke_meter_reading
        };
      }

      const d = blankForm();

      this.form = {
        initial_calibration: {
          ambient_temperature: i.ambient_temperature != null ? i.ambient_temperature : d.initial_calibration.ambient_temperature,
          garage_filter:       mergeRow(i.garage_filter,      d.initial_calibration.garage_filter),
          reference_filter_1:  mergeRow(i.reference_filter_1, d.initial_calibration.reference_filter_1),
          reference_filter_2:  mergeRow(i.reference_filter_2, d.initial_calibration.reference_filter_2),
          reference_filter_3:  mergeRow(i.reference_filter_3, d.initial_calibration.reference_filter_3)
        },
        final_calibration: {
          ambient_temperature: f.ambient_temperature != null ? f.ambient_temperature : d.final_calibration.ambient_temperature,
          garage_filter:       mergeRow(f.garage_filter,      d.final_calibration.garage_filter),
          reference_filter_1:  mergeRow(f.reference_filter_1, d.final_calibration.reference_filter_1),
          reference_filter_2:  mergeRow(f.reference_filter_2, d.final_calibration.reference_filter_2),
          reference_filter_3:  mergeRow(f.reference_filter_3, d.final_calibration.reference_filter_3)
        }
      };

      this.hasUserEdited = false;
    },

    getCurrentData() {
      return JSON.parse(JSON.stringify(this.form));
    },

    async copyInitialToFinal() {
      const ok = await this.confirmAction({
        message: "Do you want to copy initial calibration values to final calibration?",
        header: "Confirm Copy",
        acceptLabel: "Yes",
        rejectLabel: "No"
      });
      if (!ok) return;

      this.form.final_calibration = JSON.parse(JSON.stringify(this.form.initial_calibration));
    }
  }
};
</script>
